(() => {
  const pictures = new Map();
  let frame = 0;
  let lastUpdate = 0;
  let trailingUpdate;

  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const now = performance.now();
      if (now - lastUpdate < 60) {
        clearTimeout(trailingUpdate);
        trailingUpdate = setTimeout(schedule, 60);
        return;
      }
      lastUpdate = now;
      update();
    });
  }

  function picture(url) {
    if (!url) return null;
    if (pictures.has(url)) return pictures.get(url);
    const entry = { pixels: null };
    pictures.set(url, entry);
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 256 / Math.max(image.naturalWidth, image.naturalHeight));
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        entry.pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        entry.width = canvas.width;
        entry.height = canvas.height;
        entry.naturalWidth = image.naturalWidth;
        entry.naturalHeight = image.naturalHeight;
      } catch {
        entry.pixels = null;
      }
      schedule();
    };
    image.src = url;
    return entry;
  }

  function offset(position, space) {
    if (position === 'left' || position === 'top') return 0;
    if (position === 'right' || position === 'bottom') return space;
    if (position === 'center' || !position) return space / 2;
    return position.endsWith('%') ? space * parseFloat(position) / 100 : parseFloat(position) || 0;
  }

  function imagePixel(element, x, y, style) {
    const isImage = element.tagName === 'IMG';
    const background = style.backgroundImage.match(/^url\(["']?(.*?)["']?\)$/);
    const url = isImage ? element.currentSrc || element.src : background && background[1];
    const entry = picture(url);
    if (!entry?.pixels) return null;
    const rect = element.getBoundingClientRect();
    let width = rect.width;
    let height = rect.height;
    const fit = isImage ? style.objectFit : style.backgroundSize;
    if (fit === 'contain' || fit === 'cover' || fit === 'scale-down' || fit === 'none') {
      let scale = fit === 'none' ? 1 : Math[fit === 'cover' ? 'max' : 'min'](
        rect.width / entry.naturalWidth, rect.height / entry.naturalHeight
      );
      if (fit === 'scale-down') scale = Math.min(1, scale);
      width = entry.naturalWidth * scale;
      height = entry.naturalHeight * scale;
    }
    const positions = (isImage ? style.objectPosition : style.backgroundPosition).split(' ');
    const left = rect.left + offset(positions[0], rect.width - width);
    const top = rect.top + offset(positions[1], rect.height - height);
    const u = (x - left) / width;
    const v = (y - top) / height;
    if (u < 0 || u >= 1 || v < 0 || v >= 1) return null;
    const index = (Math.floor(v * entry.height) * entry.width + Math.floor(u * entry.width)) * 4;
    return [entry.pixels[index], entry.pixels[index + 1], entry.pixels[index + 2], entry.pixels[index + 3] / 255];
  }

  function backgroundAt(x, y) {
    const layers = document.elementsFromPoint(x, y);
    const result = [0, 0, 0];
    let remaining = 1;
    const blend = (color, opacity = 1) => {
      if (!color) return;
      const alpha = color[3] * opacity;
      for (let i = 0; i < 3; i++) result[i] += color[i] * alpha * remaining;
      remaining *= 1 - alpha;
    };
    for (const element of layers) {
      if (element.closest('[data-theme-header], #PBarNextFrame, [role="dialog"]')) continue;
      const style = getComputedStyle(element);
      const opacity = Number(style.opacity);
      blend(imagePixel(element, x, y, style), opacity);
      const values = style.backgroundColor.match(/[\d.]+/g);
      if (values?.length >= 3) blend([Number(values[0]), Number(values[1]), Number(values[2]), values[3] === undefined ? 1 : Number(values[3])], opacity);
      if (remaining < 0.01) break;
    }
    return result.map(channel => channel + 255 * remaining);
  }

  function luminance(color) {
    const linear = color.map(channel => {
      const value = channel / 255;
      return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    });
    return linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722;
  }

  function update() {
    const header = document.getElementById('SiteHeader');
    if (!header) return;
    const menuOpen = header.classList.contains('ooo-store-open');
    const groups = new Map();
    header.querySelectorAll('nav a, nav button').forEach(item => {
      if (item.closest('.ooo-store-mega-menu, [data-header-subnav-wrapper], [data-header-subnav-block]')) return;
      const visibleImage = Array.from(item.querySelectorAll('img')).find(image => image.getBoundingClientRect().width > 0);
      const rect = (visibleImage || item).getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const group = item.closest('ul') || item;
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push({ item, rect });
    });
    groups.forEach(items => {
      let mode = 'dark';
      if (!menuOpen) {
        const light = items.reduce((total, { rect }) => {
          const y = Math.max(0, Math.min(innerHeight - 1, rect.top + rect.height / 2));
          return total + [0.2, 0.5, 0.8].reduce((sum, fraction) => {
            const x = Math.max(0, Math.min(innerWidth - 1, rect.left + rect.width * fraction));
            return sum + luminance(backgroundAt(x, y)) / (3 * items.length);
          }, 0);
        }, 0);
        const previous = items[0].item.dataset.oooHeaderContrast;
        const threshold = previous === 'light' ? 0.20 : previous === 'dark' ? 0.16 : 0.179;
        if (light < threshold) mode = 'light';
      }
      items.forEach(({ item }) => {
        if (item.dataset.oooHeaderContrast !== mode) item.dataset.oooHeaderContrast = mode;
      });
    });
  }

  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule);
  addEventListener('load', schedule);
  addEventListener('ooo-store-menu-change', schedule);
  document.addEventListener('load', schedule, true);
  document.addEventListener('change', schedule);
  document.addEventListener('transitionend', schedule);
  document.addEventListener('shopify:section:load', schedule);
  const content = document.getElementById('MainContent');
  if (content) new MutationObserver(schedule).observe(content, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'class', 'style', 'hidden'] });
  schedule();
})();
